import dayjs from 'dayjs';

/**
 * Auto-dispatch algorithm for employee scheduling
 */

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

/**
 * Gets employee schedule for a specific day
 * @param {Object} employee - Employee object
 * @param {Date} date - Target date
 * @returns {Object|null} - Schedule object or null if not working
 */
export const getEmployeeScheduleForDay = (employee, date) => {
  const dayName = DAYS_OF_WEEK[date.getDay()];
  return employee.schedule?.[dayName] || null;
};

/**
 * Calculates shift duration in minutes
 * @param {Object} schedule - Employee schedule for the day
 * @returns {number} - Shift duration in minutes
 */
export const calculateShiftDuration = (schedule) => {
  if (!schedule?.startTime || !schedule?.endTime) return 0;

  const start = dayjs(`2024-01-01 ${schedule.startTime}`);
  const end = dayjs(`2024-01-01 ${schedule.endTime}`);
  return end.diff(start, 'minutes');
};

/**
 * Filters and prioritizes rooms for an employee
 * @param {Array} rooms - All rooms
 * @param {Array} items - All items for cost/difficulty calculation
 * @param {string} employeeType - Employee type
 * @param {string} priorityMethod - 'difficulty' or 'cost'
 * @returns {Array} - Filtered and sorted rooms
 */
export const filterAndPrioritizeRooms = (rooms, items, employeeType, priorityMethod) => {
  // Filter rooms that need work and aren't completed
  const availableRooms = rooms.filter(room => {
    if (room.status === 'Up' || room.status === 'Occupied') return false;

    if (employeeType === 'maintenance') {
      return room.selectedMaintenanceRequirements &&
             room.selectedMaintenanceRequirements.toLowerCase() !== 'completed';
    } else if (employeeType === 'housekeeping') {
      return room.selectedHousekeepingRequirements &&
             room.selectedHousekeepingRequirements.toLowerCase() !== 'completed';
    }

    return false;
  });

  // Calculate priority score for each room
  const roomsWithScores = availableRooms.map(room => {
    let score = 0;

    // Difficulty and cost are maintained server-side and stored on each unit
    // as `calculatedDifficulty` / `calculatedCost`. Read them from the document.
    if (priorityMethod === 'difficulty') {
      score = room.calculatedDifficulty || 0;
    } else if (priorityMethod === 'cost') {
      score = room.calculatedCost || 0;
    }

    return { ...room, priorityScore: score };
  });

  // Sort by LOWEST score first (easiest/cheapest first)
  return roomsWithScores.sort((a, b) => a.priorityScore - b.priorityScore);
};

/**
 * Determines room work sequence based on requirements
 * @param {Object} room - Room object
 * @returns {Array} - Array of work phases
 */
export const determineRoomWorkSequence = (room) => {
  const hkRequirement = room.selectedHousekeepingRequirements?.toLowerCase() || '';
  const mtRequirement = room.selectedMaintenanceRequirements?.toLowerCase() || '';

  const sequence = [];

  // Check if both departments need to work on this room
  const needsMaintenance = mtRequirement && mtRequirement !== 'completed' && mtRequirement !== 'please select';
  const needsHousekeeping = hkRequirement && hkRequirement !== 'completed' && hkRequirement !== 'please select';

  if (!needsMaintenance && !needsHousekeeping) return [];

  // Heavy housekeeping goes first
  if (needsHousekeeping && ['heavy', 'very heavy', 'trashout'].includes(hkRequirement)) {
    sequence.push({ type: 'housekeeping', phase: 'initial' });
    if (needsMaintenance) {
      sequence.push({ type: 'maintenance', phase: 'middle' });
      sequence.push({ type: 'housekeeping', phase: 'final' });
    }
  } else {
    // Normal sequence: maintenance first, then housekeeping
    if (needsMaintenance) {
      sequence.push({ type: 'maintenance', phase: 'initial' });
    }
    if (needsHousekeeping) {
      sequence.push({ type: 'housekeeping', phase: needsMaintenance ? 'final' : 'initial' });
    }
  }

  return sequence;
};

/**
 * Calculates work duration for a room and employee type
 * @param {Object} room - Room object
 * @param {Array} items - Items array
 * @param {string} employeeType - Employee type
 * @returns {number} - Duration in minutes
 */
export const calculateWorkDuration = (room, items, employeeType) => {
  // Difficulty points map directly to minutes. We need the per-department
  // portion (housekeeping vs maintenance), which isn't stored on the document,
  // so compute just the relevant slice here from the requirement + item points.
  let relevantDifficulty = 0;

  if (employeeType === 'maintenance') {
    const mtRequirement = room.selectedMaintenanceRequirements?.toLowerCase() || '';
    const baseWeights = {
      refresh: 15, easy: 45, medium: 60, heavy: 90, "very heavy": 120
    };
    relevantDifficulty = baseWeights[mtRequirement] || 0;

    // Add item difficulties for maintenance items
    if (items && items.length > 0) {
      const itemsDict = {};
      items.forEach(item => {
        if (item.itemID) itemsDict[item.itemID] = item;
      });

      Object.keys(room).forEach(key => {
        if (key.startsWith('itemCount') && room[key] > 0) {
          const item = itemsDict[key];
          if (item && item.itemDifficulty && item.itemResponsibility === 'Maintenance') {
            const difficulty = parseInt(item.itemDifficulty, 10) || 0;
            const count = parseInt(room[key], 10) || 0;
            relevantDifficulty += difficulty * count;
          }
        }
      });
    }
  } else if (employeeType === 'housekeeping') {
    const hkRequirement = room.selectedHousekeepingRequirements?.toLowerCase() || '';
    const baseWeights = {
      refresh: 15, easy: 45, medium: 60, heavy: 90, "very heavy": 120
    };
    relevantDifficulty = baseWeights[hkRequirement] || 0;

    // Add item difficulties for housekeeping items
    if (items && items.length > 0) {
      const itemsDict = {};
      items.forEach(item => {
        if (item.itemID) itemsDict[item.itemID] = item;
      });

      Object.keys(room).forEach(key => {
        if (key.startsWith('itemCount') && room[key] > 0) {
          const item = itemsDict[key];
          if (item && item.itemDifficulty && item.itemResponsibility === 'Housekeeping') {
            const difficulty = parseInt(item.itemDifficulty, 10) || 0;
            const count = parseInt(room[key], 10) || 0;
            relevantDifficulty += difficulty * count;
          }
        }
      });
    }
  }

  // Convert difficulty points to minutes (difficulty points = minutes needed)
  return relevantDifficulty;
};

/**
 * Creates break schedule for an employee
 * @param {Object} schedule - Employee schedule
 * @param {string} employeeType - Employee type
 * @returns {Array} - Array of break objects
 */
export const createBreakSchedule = (schedule, employeeType) => {
  if (!schedule?.startTime || !schedule?.endTime) return [];

  const breaks = [];
  const shiftStart = dayjs(`2024-01-01 ${schedule.startTime}`);
  const shiftEnd = dayjs(`2024-01-01 ${schedule.endTime}`);
  const shiftDuration = shiftEnd.diff(shiftStart, 'minutes');

  // Lunch at 4 hours into shift
  const lunchTime = shiftStart.add(4, 'hours');
  breaks.push({
    type: 'Lunch',
    startTime: lunchTime.format('HH:mm'),
    duration: 60,
    location: 'lobby'
  });

  console.log(`Created lunch break at ${lunchTime.format('HH:mm')} for ${employeeType} employee`);

  if (employeeType === 'maintenance') {
    // First break after 1 hour of portering
    const firstBreak = shiftStart.add(70, 'minutes'); // 60min porter + 10min break
    breaks.push({
      type: 'Break',
      startTime: firstBreak.format('HH:mm'),
      duration: 10,
      location: 'lobby'
    });

    // Second break halfway between lunch end and shift end
    const lunchEnd = lunchTime.add(60, 'minutes');
    const remainingTime = shiftEnd.diff(lunchEnd, 'minutes');
    const secondBreak = lunchEnd.add(remainingTime / 2, 'minutes');
    breaks.push({
      type: 'Break',
      startTime: secondBreak.format('HH:mm'),
      duration: 10,
      location: 'lobby'
    });
  } else {
    // First break halfway between start and lunch
    const timeToLunch = lunchTime.diff(shiftStart, 'minutes');
    const firstBreak = shiftStart.add(timeToLunch / 2, 'minutes');
    breaks.push({
      type: 'Break',
      startTime: firstBreak.format('HH:mm'),
      duration: 10,
      location: 'lobby'
    });

    // Second break halfway between lunch end and shift end
    const lunchEnd = lunchTime.add(60, 'minutes');
    const remainingTime = shiftEnd.diff(lunchEnd, 'minutes');
    const secondBreak = lunchEnd.add(remainingTime / 2, 'minutes');
    breaks.push({
      type: 'Break',
      startTime: secondBreak.format('HH:mm'),
      duration: 10,
      location: 'lobby'
    });
  }

  return breaks.sort((a, b) => a.startTime.localeCompare(b.startTime));
};

/**
 * Main auto-dispatch algorithm
 * @param {Object} params - Algorithm parameters
 * @returns {Object} - Dispatch results
 */
export const generateAutoDispatch = async (params) => {
  const {
    employee,
    selectedDate,
    rooms,
    items,
    priorityMethod,
    clientId,
    selectedProperty,
    allEmployees
  } = params;

  // Get employee schedule for the selected day
  const schedule = getEmployeeScheduleForDay(employee, selectedDate);
  if (!schedule) {
    return {
      success: false,
      message: `${employee.name} is not scheduled to work on this day`,
      tasks: []
    };
  }

  const shiftStart = dayjs(`2024-01-01 ${schedule.startTime}`);
  const shiftEnd = dayjs(`2024-01-01 ${schedule.endTime}`);
  const shiftDuration = shiftEnd.diff(shiftStart, 'minutes');

  console.log(`Generating dispatch for ${employee.name} (${employee.type})`);
  console.log(`Shift: ${schedule.startTime} - ${schedule.endTime} (${shiftDuration} minutes)`);

  const tasks = [];
  let currentTime = shiftStart;

  // Create break schedule (this is unique per employee, not affected by load balancing)
  const breaks = createBreakSchedule(schedule, employee.type);
  console.log('Break schedule for', employee.name, ':', breaks);

  // Add initial portering for maintenance
  if (employee.type === 'maintenance') {
    tasks.push({
      task: 'Porter',
      location: 'grounds',
      startTime: currentTime.toDate(),
      endTime: currentTime.add(60, 'minutes').toDate(),
      notes: 'Auto-generated: Initial portering'
    });
    currentTime = currentTime.add(60, 'minutes');
  }

  // Get prioritized rooms
  const prioritizedRooms = filterAndPrioritizeRooms(rooms, items, employee.type, priorityMethod);
  console.log(`Found ${prioritizedRooms.length} rooms for ${employee.type}`);

  // Perform load balancing if we have the required parameters
  let finalRoomAssignments = prioritizedRooms;
  let loadBalancingInfo = null;

  if (selectedProperty && allEmployees) {
    const { performLoadBalancing } = await import('./loadBalancingUtils');

    loadBalancingInfo = await performLoadBalancing({
      clientId,
      selectedProperty,
      allEmployees,
      currentEmployee: employee,
      selectedDate,
      prioritizedRooms,
      employeeType: employee.type
    });

    finalRoomAssignments = loadBalancingInfo.assignedRooms;
    console.log(`After load balancing: ${finalRoomAssignments.length} rooms assigned to ${employee.name}`);
  } else {
    console.log('Load balancing skipped - missing selectedProperty or allEmployees');
  }

  let roomIndex = 0;
  let currentRoomWork = null;

  // Main scheduling loop
  while (currentTime.valueOf() < shiftEnd.valueOf()) {
    console.log(`\n=== LOOP ITERATION: Current time ${currentTime.format('HH:mm')} ===`);
    console.log(`Remaining breaks:`, breaks.map(b => `${b.type} at ${b.startTime}`));

    // Check if it's time for a break (look ahead up to 1 hour)
    const upcomingBreak = breaks.find(breakItem => {
      const breakTime = dayjs(`2024-01-01 ${breakItem.startTime}`);
      const timeWindow = currentTime.add(60, 'minutes');
      const isUpcoming = breakTime.valueOf() <= timeWindow.valueOf() &&
                        breakTime.valueOf() >= currentTime.valueOf();

      console.log(`Checking break ${breakItem.type} at ${breakItem.startTime}: ${isUpcoming ? 'UPCOMING' : 'not yet'}`);

      return isUpcoming;
    });

    if (upcomingBreak) {
      const breakStart = dayjs(`2024-01-01 ${upcomingBreak.startTime}`);

      // If we're in the middle of room work, split it
      if (currentRoomWork) {
        const workUntilBreak = breakStart.diff(currentTime, 'minutes');
        if (workUntilBreak > 0) {
          tasks.push({
            task: 'Room Turn',
            location: `${currentRoomWork.room.displayName} - ${currentRoomWork.requirement}`,
            startTime: currentTime.toDate(),
            endTime: breakStart.toDate(),
            notes: `Auto-generated: ${currentRoomWork.type} work (Part ${currentRoomWork.part})`
          });

          currentRoomWork.remainingTime -= workUntilBreak;
        }
      }

      // Add the break
      const breakTask = {
        task: upcomingBreak.type,
        location: upcomingBreak.location,
        startTime: breakStart.toDate(),
        endTime: breakStart.add(upcomingBreak.duration, 'minutes').toDate(),
        notes: 'Auto-generated break'
      };
      tasks.push(breakTask);
      console.log(`✅ ADDED BREAK: ${upcomingBreak.type} from ${breakStart.format('HH:mm')} to ${breakStart.add(upcomingBreak.duration, 'minutes').format('HH:mm')}`);

      currentTime = breakStart.add(upcomingBreak.duration, 'minutes');

      // Remove this break from the list
      const breakIndex = breaks.indexOf(upcomingBreak);
      breaks.splice(breakIndex, 1);
      console.log(`Removed break from list. ${breaks.length} breaks remaining.`);
      continue;
    }

    // Continue room work if we were in the middle of something
    if (currentRoomWork && currentRoomWork.remainingTime > 0) {
      const timeUntilNextEvent = getTimeUntilNextEvent(currentTime, shiftEnd, breaks);
      const workTime = Math.min(currentRoomWork.remainingTime, timeUntilNextEvent);

      if (workTime > 0) {
        tasks.push({
          task: 'Room Turn',
          location: `${currentRoomWork.room.displayName} - ${currentRoomWork.requirement}`,
          startTime: currentTime.toDate(),
          endTime: currentTime.add(workTime, 'minutes').toDate(),
          notes: `Auto-generated: ${currentRoomWork.type} work (Part ${currentRoomWork.part})`
        });

        currentTime = currentTime.add(workTime, 'minutes');
        currentRoomWork.remainingTime -= workTime;

        if (currentRoomWork.remainingTime <= 0) {
          currentRoomWork = null;
        }
      } else {
        break;
      }
      continue;
    }

    // Start new room work
    if (roomIndex < finalRoomAssignments.length) {
      const room = finalRoomAssignments[roomIndex];
      const workDuration = calculateWorkDuration(room, items, employee.type);

      if (workDuration > 0) {
        const requirement = employee.type === 'maintenance'
          ? room.selectedMaintenanceRequirements
          : room.selectedHousekeepingRequirements;

        currentRoomWork = {
          room,
          type: employee.type,
          requirement,
          remainingTime: workDuration,
          part: 1
        };

        console.log(`Starting work on ${room.displayName}: ${workDuration} minutes of ${employee.type} work`);
      }

      roomIndex++;
    } else {
      // No more rooms, fill remaining time with porter work or other tasks
      const remainingTime = shiftEnd.diff(currentTime, 'minutes');
      if (remainingTime > 0) {
        tasks.push({
          task: 'Porter',
          location: 'grounds',
          startTime: currentTime.toDate(),
          endTime: shiftEnd.toDate(),
          notes: 'Auto-generated: Additional portering'
        });
      }
      break;
    }
  }

  return {
    success: true,
    message: `Generated ${tasks.length} tasks for ${employee.name}`,
    tasks,
    unassignedRooms: finalRoomAssignments.slice(roomIndex),
    loadBalancingInfo
  };
};

/**
 * Helper function to calculate time until next break or end of shift
 */
const getTimeUntilNextEvent = (currentTime, shiftEnd, breaks) => {
  let nextEventTime = shiftEnd;

  for (const breakItem of breaks) {
    const breakTime = dayjs(`2024-01-01 ${breakItem.startTime}`);
    if (breakTime.valueOf() > currentTime.valueOf() && breakTime.valueOf() < nextEventTime.valueOf()) {
      nextEventTime = breakTime;
    }
  }

  return nextEventTime.diff(currentTime, 'minutes');
};